/**
 * The route-test optimiser: whose numbers to read, then Formula and Rules
 * on their own tabs. Drop stats are the default. 21/10/2026: today's test
 * drops ranked by the formula being edited sit beside them (on the right
 * when wide) — the order the engine reads for the winner.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import {
  lockRouteTestRanking,
  routeTestRankingOf,
  routeTestClockOnCountry,
  routeTestDayProblem,
  routeTestSource,
  type DropGenGatewayRanking,
  type RouteTestClock,
} from '@trafficpulse/shared';

import type { ContainerDraft, DropGenPlanDetail } from '../../../drop-generator.types';
import { planDay } from '../../../utils/clock/plan-day';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { useSectionSources } from '../master/section-sources';
import type { ContainerEditor } from '../state/useContainerEditor';
import { ClockNote, NumbersFrom } from './step/ClockNote';
import { RankingResult } from './step/RankingResult';
import { RankingTabs } from './step/RankingTabs';

export const RouteTestStep: FC<{
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  /** The saved route test; null while it is new (no drops to rank yet). */
  containerId?: string | null;
}> = ({ editor, plan, containerId = null }) => {
  // 20/10/2026 — an untouched generic default becomes the delivery default (Delivered Rate on its drops).
  const ranking = routeTestRankingOf(editor.draft.gateway.ranking);
  const page = useCountryClock();
  const sources = useSectionSources();
  // The draft's start and pins are Danish; the window is the country's (18/10/2026).
  const clock = routeTestClockOnCountry({ ...routeClock(editor.draft), strategy: sources.master?.gateway?.strategy ?? editor.draft.gateway.strategy }, planDay(plan, page.runDate));
  const problem = routeTestDayProblem([clock], plan.windowStartMin);
  const setRanking = (next: DropGenGatewayRanking) => editor.gatewayOp({ kind: 'ranking', ranking: lockRouteTestRanking(next) });
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(340px,0.85fr)]">
      <div className="flex min-w-0 flex-col gap-4">
        <ClockNote clock={clock} problem={problem} windowStartMin={plan.windowStartMin} page={{ ...page, tz: page.tz ?? plan.recipientTimezone }} />
        <NumbersFrom source={routeTestSource(ranking)} onChange={(next) => setRanking({ ...ranking, source: next })} />
        <label className="flex items-center gap-3 text-xs">
          Wait after the last test drop
          <input
            type="number"
            min={0}
            max={1440}
            value={ranking.routeTestSettleMin ?? 30}
            onChange={(event) => setRanking({ ...ranking, routeTestSettleMin: Number(event.target.value) })}
            className="h-8 w-20 rounded-md border border-input bg-background px-2 font-mono"
          />
          minutes before choosing a winner
        </label>
        <p className="text-xs text-muted-foreground">
          The master uses this result only once every test drop has been sent and its stats are in. To compare senders fairly, give each gateway
          row one sender ID. You can also choose a gateway or a list on each drop slot.
        </p>
        <RankingTabs ranking={ranking} onChange={setRanking} />
      </div>
      <div className="min-w-0 xl:sticky xl:top-2 xl:self-start">
        <RankingResult countryKey={plan.countryKey} containerId={containerId} ranking={ranking} />
      </div>
    </div>
  );
};

function routeClock(draft: ContainerDraft): RouteTestClock {
  return {
    enabled: draft.enabled,
    routeTest: true,
    strategy: draft.gateway.strategy ?? '',
    startMode: draft.startMode,
    startAtMin: draft.startAtMin,
    slots: draft.slots.map((slot, orderIndex) => ({ enabled: slot.enabled, orderIndex, pinnedAtMin: slot.pinnedAtMin })),
  };
}
